import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import { useModalStore } from '@/store/modal';
import { useTranslation } from '@/hooks/useTranslation';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { resolvePaymentRetrySettingsPage } from '@/constants/utils/webCheckoutRouting';
import { trackWhopPurchase } from '@/constants/utils/analytics';
import { trackPurchaseCompleted, trackPurchaseFailed } from '@/constants/utils/analyticsEvents';
import { whopApi, creditPacksApi } from '@/lib/api';
import { applyWhopCheckoutOptIn, whopCheckoutReturnPath } from '@/lib/whopElements';
import { rememberCheckoutSignInReturn } from '@/lib/checkoutSignInReturn';

const REFRESH_INTERVAL_MS = 2000;
const REFRESH_ATTEMPTS = 5;
// Polls allowed after a recorded decline before the page settles on failure.
const DECLINE_GRACE_ATTEMPTS = 6;

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.solid_color_primary,
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    gap: 16,
    paddingVertical: 40,
    paddingHorizontal: 28,
    borderRadius: 16,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary || colors.solid_color_secondary,
    marginBottom: 4,
  },
  actions: {
    width: '100%',
    gap: 10,
    marginTop: 8,
  },
});

export default function PaymentSuccessfulPage() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const router = useRouter();
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const fetchUserCredits = useAuthStore((state) => state.fetchUserCredits);
  const userId = useAuthStore((state) => state.user?.id);
  // Processors that bounce the buyer to their bank for 3D Secure return here
  // with ?status=error when the payment failed or was canceled. Nothing was
  // purchased in that case, so this page must not claim success.
  const { status, checkoutType, checkoutContext } = useLocalSearchParams();
  const contextId = typeof checkoutContext === 'string' ? checkoutContext : null;
  const redirectFailed = ['error', 'failed', 'canceled', 'cancelled'].includes(status);
  const retrySettingsPage = resolvePaymentRetrySettingsPage(checkoutType);
  const [refreshing, setRefreshing] = useState(!redirectFailed);
  const attemptsRef = useRef(0);
  const declinedAtAttemptRef = useRef(null);
  const reportedDeclineRef = useRef(false);
  const [confirmation, setConfirmation] = useState(contextId ? 'pending' : 'legacy');
  // Elements returns a declined off-site (3DS/bank) step to this page without a
  // status parameter; the webhook-recorded decline is the only signal.
  const failed = redirectFailed || confirmation === 'failed';
  const [autoNotice, setAutoNotice] = useState('');
  const [returnPath, setReturnPath] = useState('/app');

  // A buyer sent to their bank for 3D Secure leaves the page mid-checkout, so
  // this redirect is the only place their outcome is observable client-side.
  // `trackPurchaseCompleted` dedupes against the in-page completion callback for
  // the buyers who never left.
  useEffect(() => {
    const checkoutTypeName = typeof checkoutType === 'string' ? checkoutType : undefined;
    // Swapping a card returns through this same page and buys nothing.
    if (checkoutTypeName === 'update-payment' || contextId) return;
    if (redirectFailed) {
      trackPurchaseFailed({ checkoutType: checkoutTypeName, reason: 'processor_redirect' });
      return;
    }
    trackPurchaseCompleted({ checkoutType: checkoutTypeName });
  }, [redirectFailed, checkoutType, contextId]);

  useEffect(() => {
    if (redirectFailed || contextId) return;
    trackWhopPurchase({ id: `payment-successful:${checkoutType || 'unknown'}` });
  }, [redirectFailed, checkoutType, contextId]);

  useEffect(() => {
    setConfirmation(contextId ? 'pending' : 'legacy');
    setRefreshing(!redirectFailed && (!contextId || Boolean(userId)));
    setAutoNotice('');
    setReturnPath(whopCheckoutReturnPath(contextId, userId));
    if (redirectFailed) return undefined;
    if (contextId && !userId) return undefined;

    let cancelled = false;
    let timer = null;
    attemptsRef.current = 0;
    declinedAtAttemptRef.current = null;
    reportedDeclineRef.current = false;

    const refresh = async () => {
      try {
        if (contextId) {
          const result = await whopApi.getCheckoutStatus(contextId);
          if (cancelled || useAuthStore.getState().user?.id !== userId) return;
          if (result.status === 'held') {
            setConfirmation('held');
            setRefreshing(false);
            return;
          }
          if (result.status === 'completed') {
            await Promise.allSettled([fetchUser?.(), fetchUserCredits?.({ force: true })]);
            if (cancelled || useAuthStore.getState().user?.id !== userId) return;
            const notice = await applyWhopCheckoutOptIn({
              contextId, userId, payment: result,
              saveSettings: creditPacksApi.updateAutoTopupSettings,
              applySettings: (settings) => {
                if (useAuthStore.getState().user?.id === userId) useAuthStore.getState().updateAutoTopupSettings(settings);
              },
            });
            if (!cancelled) setAutoNotice(notice);
            if (cancelled) return;
            trackPurchaseCompleted({ checkoutType: result.checkoutType, processor: 'whop' });
            trackWhopPurchase({ id: result.paymentId });
            setConfirmation('completed');
            setRefreshing(false);
            return;
          }
          // A recorded decline is not final by itself: the buyer may have paid
          // on a second attempt whose webhook is still in flight. Show retry
          // once that wait is long enough to read, but keep listening for the
          // rest of the confirmation window so a late receipt can replace it.
          if (result.status === 'failed') {
            if (declinedAtAttemptRef.current === null) declinedAtAttemptRef.current = attemptsRef.current;
          } else if (result.status === 'pending' && reportedDeclineRef.current) {
            reportedDeclineRef.current = false;
            declinedAtAttemptRef.current = null;
            setConfirmation('pending');
            setRefreshing(true);
          } else {
            declinedAtAttemptRef.current = null;
          }
        }
        const tasks = [];
        if (fetchUser) tasks.push(fetchUser());
        if (fetchUserCredits) tasks.push(fetchUserCredits({ force: true }));
        await Promise.all(tasks);
      } catch (err) {
        // The receipt belongs to another account or is unknown. Polling cannot
        // confirm it for this user, and "still processing" would be misleading.
        if (contextId && err?.status === 404) {
          if (cancelled || useAuthStore.getState().user?.id !== userId) return;
          setConfirmation('unknown');
          setRefreshing(false);
          return;
        }
        console.warn('[payment-successful] Failed to refresh account after purchase:', err);
      }

      attemptsRef.current += 1;
      if (cancelled) return;

      // Webhooks can land a beat after the processor redirects. Keep polling
      // briefly so the account state catches up before the user leaves.
      const hardCap = contextId ? 15 : REFRESH_ATTEMPTS;
      const declinedAt = declinedAtAttemptRef.current;
      const declineLimit = declinedAt === null ? hardCap : Math.min(hardCap, declinedAt + DECLINE_GRACE_ATTEMPTS);
      if (!reportedDeclineRef.current && declinedAt !== null && attemptsRef.current >= declineLimit) {
        reportedDeclineRef.current = true;
        setConfirmation('failed');
        setRefreshing(false);
        trackPurchaseFailed({
          checkoutType: typeof checkoutType === 'string' ? checkoutType : undefined,
          processor: 'whop', reason: 'processor_declined',
        });
      }
      if (attemptsRef.current < hardCap) {
        timer = setTimeout(refresh, REFRESH_INTERVAL_MS);
        return;
      }
      setRefreshing(false);
    };

    refresh();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [redirectFailed, contextId, userId, fetchUser, fetchUserCredits, checkoutType]);

  const needsSignIn = !failed && contextId && !userId;
  const pending = !failed && confirmation === 'pending';
  const held = !failed && confirmation === 'held';
  const unknown = !failed && confirmation === 'unknown';
  const title = failed ? t('pages.paymentSuccessful.errorTitle')
    : needsSignIn ? 'Sign in to confirm your purchase'
      : held ? 'Your account is under review'
      : unknown ? 'This purchase isn’t on this account'
      : pending ? (refreshing ? 'Confirming your purchase' : 'Your payment is still processing')
        : t('pages.paymentSuccessful.title');
  const subtitle = failed ? t('pages.paymentSuccessful.errorSubtitle')
    : needsSignIn ? 'Sign in with the account you purchased for, or return to the Gab AI app to see your updated account. There’s no need to pay again.'
      : held ? 'Please contact support for help with your purchase.'
      : unknown ? 'Sign in with the account you purchased for to see it, or contact support if you paid with this one. There’s no need to pay again.'
      : pending ? 'We’ll update your account once payment is confirmed. You can return to Gab AI; there’s no need to pay again.'
        : t('pages.paymentSuccessful.subtitle');

  return (
    <View style={styles.container}>
      <MetaHead
        title={title}
        description={t('pages.paymentSuccessful.metaDescription')}
        noindex
      />
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          {failed || held || unknown ? (
            <Icon name="circle-exclamation" size={32} color={colors.text_color_primary} solid />
          ) : refreshing ? (
            <ActivityIndicator size="small" color={colors.text_color_primary} />
          ) : (
            <Icon name={pending ? 'clock' : 'circle-check'} size={32} color={colors.text_color_primary} solid />
          )}
        </View>
        <Text size="xl" weight="bold" style={{ textAlign: 'center' }}>
          {title}
        </Text>
        <Text size="m" color="secondary" style={{ textAlign: 'center' }}>
          {subtitle}
        </Text>
        {!!autoNotice && <Text size="s" color="secondary" style={{ textAlign: 'center' }}>{autoNotice}</Text>}
        <View style={styles.actions}>
          {failed ? (
            <>
              <Button
                variant="primary"
                text={t('pages.paymentSuccessful.errorRetry')}
                onPress={() => router.replace(`/settings?page=${retrySettingsPage}`)}
                isFullWidth
              />
              <Button
                variant="secondary"
                text={t('pages.paymentSuccessful.continue')}
                onPress={() => router.replace(returnPath)}
                isFullWidth
              />
              <Text size="s" color="secondary" style={{ textAlign: 'center' }}>
                {t('pages.paymentSuccessful.errorContact')}
              </Text>
            </>
          ) : (
            <>
              {needsSignIn && <Button
                variant="primary" text="Sign in" isFullWidth
                onPress={() => {
                  rememberCheckoutSignInReturn({ contextId, checkoutType });
                  useModalStore.getState().actions.openModal('LoginModal');
                }}
              />}
              <Button
                variant={needsSignIn ? 'secondary' : 'primary'}
                text={t('pages.paymentSuccessful.continue')}
                onPress={() => router.replace(returnPath)}
                isFullWidth
              />
              {!needsSignIn && !unknown && <Button
                variant="secondary"
                text={t('pages.paymentSuccessful.viewCredits')}
                onPress={() => router.replace('/settings?page=credits')}
                isFullWidth
              />}
            </>
          )}
        </View>
      </View>
    </View>
  );
}
